<template>
  <div class="space-y-6">
    <!-- Setup status: only the providers this school has added -->
    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="payment-setup-status">
      <div class="px-5 py-4 border-b border-gray-100">
        <h2 class="text-base font-bold text-gray-900">Setup status</h2>
        <p class="text-xs text-gray-500 mt-0.5">Whether each of your payment providers is ready to take payments</p>
      </div>
      <div class="p-5">
        <div v-if="(loadingSchool || loadingProviderStatus) && !setupRows.length" class="flex items-center justify-center py-8">
          <i class="pi pi-spin pi-spinner text-2xl text-primary"></i>
        </div>
        <p v-else-if="!setupRows.length" class="text-sm text-gray-500" data-testid="payment-setup-status-empty">
          No payment providers added yet. Add one below so students and parents can pay online.
        </p>
        <div v-else class="space-y-2">
          <div v-for="row in setupRows" :key="row.name"
            class="flex items-start justify-between gap-4 p-3 rounded-lg border border-gray-100"
            :data-testid="`payment-status-${row.name}`">
            <div>
              <p class="text-sm font-semibold text-gray-900">{{ row.label }}</p>
              <p class="text-xs text-gray-600">{{ row.summary }}</p>
              <button v-if="setupGuideFor(row.name)" type="button"
                class="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                :data-testid="`payment-setup-guide-link-${row.name}`" @click="openSetupGuide(row.name)">
                <i class="pi pi-book text-[11px]"></i>
                Setup guide
              </button>
            </div>
            <Tag :value="row.badge" :severity="row.severity" class="shrink-0" :data-testid="`payment-status-badge-${row.name}`" />
          </div>
        </div>
      </div>
    </div>

    <!-- Fee-split consent (campus side of the dual opt-in; renders only for child schools) -->
    <FeeSplitConsentCard />

    <!-- Existing Providers -->
    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div class="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
        <div>
          <h2 class="text-base font-bold text-gray-900">Payment providers</h2>
          <p class="text-xs text-gray-500 mt-0.5">The companies that take card and bank payments for your school's bills</p>
        </div>
        <Button size="small" :icon="showForm ? 'pi pi-minus' : 'pi pi-plus'"
          :label="showForm ? 'Cancel' : 'Add provider'" data-testid="btn-toggle-provider-form" @click="toggleForm()" />
      </div>

      <!-- Add/Edit Provider Form -->
      <transition enter-active-class="transition-all duration-200 ease-out" enter-from-class="max-h-0 opacity-0"
        enter-to-class="max-h-[900px] opacity-100" leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="max-h-[900px] opacity-100" leave-to-class="max-h-0 opacity-0">
        <div v-if="showForm" class="overflow-hidden border-b border-gray-100">
          <div class="p-5 bg-gray-50/50">
            <h3 class="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i class="pi pi-credit-card text-primary"></i>
              {{ isEditing ? `Change ${providerLabel(formName)} details` : 'Add a payment provider' }}
            </h3>
            <form novalidate class="space-y-4" @submit.prevent="submitProvider">
              <div>
                <label for="provider-name" class="block text-xs font-medium text-gray-600 mb-1">Provider</label>
                <Select input-id="provider-name" :modelValue="formName" :options="providerOptions" optionLabel="label"
                  optionValue="value" optionDisabled="disabled" placeholder="Choose a provider" class="w-full"
                  :disabled="isEditing" :invalid="!!nameError" data-testid="select-provider"
                  @update:modelValue="selectProvider($event ?? '')" />
                <small v-if="nameError" class="p-error block mt-0.5" data-testid="error-provider-name">{{ nameError }}</small>
                <p v-else-if="allProvidersAdded" class="text-[11px] text-gray-500 mt-0.5" data-testid="provider-all-added-hint">
                  Every provider is already added. To change one, use the pencil button next to it below.
                </p>
                <button v-if="formName && setupGuideFor(formName)" type="button"
                  class="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  data-testid="payment-form-guide-link" @click="openSetupGuide(formName)">
                  <i class="pi pi-book text-[11px]"></i>
                  Where do I find these details?
                </button>
              </div>

              <!-- Key fields for the chosen provider -->
              <div v-if="formFields.length" class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div v-for="field in formFields" :key="field.key">
                  <label :for="`provider-${field.key}`" class="block text-xs font-medium text-gray-600 mb-1">
                    {{ field.label }}
                  </label>
                  <InputText :id="`provider-${field.key}`" v-model="formValues[field.key]"
                    :placeholder="fieldPlaceholder(field, isSaved(field))"
                    :type="field.secret ? 'password' : 'text'" autocomplete="off" class="w-full"
                    :invalid="!!fieldError(field.key)" :data-testid="`input-provider-${field.key}`" />
                  <small v-if="fieldError(field.key)" class="p-error block mt-0.5" :data-testid="`error-provider-${field.key}`">
                    {{ fieldError(field.key) }}
                  </small>
                  <p v-else class="text-[11px] text-gray-500 mt-0.5">
                    <template v-if="isSaved(field)">A key is saved. Leave this blank to keep it, or paste a new one to replace it. </template>
                    {{ field.help }}
                  </p>
                </div>
              </div>

              <Message v-if="paystackFormInTestMode" severity="warn" size="small" data-testid="paystack-test-mode-badge">
                <strong>Test mode</strong> — these are Paystack test keys, so no real money will be charged.
                Switch to the pk_live_ / sk_live_ keys to collect real payments.
              </Message>

              <!-- Interswitch — details live on PAY Items below -->
              <Message v-if="isCode(formName, PaymentProviderType.Interswitch)" severity="info" size="small" data-testid="interswitch-pay-item-note">
                Interswitch details (Merchant Code, PAY Item ID and MAC Key) are added per PAY Item. Save Interswitch
                first, then add at least one PAY Item under it — students can't pay with Interswitch until you do.
              </Message>

              <div class="flex justify-end pt-2">
                <Button size="small" type="submit" :label="isEditing ? 'Save changes' : 'Save provider'" icon="pi pi-check"
                  :loading="isSubmitting" data-testid="btn-settings-payment-save" />
              </div>
            </form>
          </div>
        </div>
      </transition>

      <!-- Provider List -->
      <div class="p-5">
        <div v-if="loadingSchool && !paymentPro.length" class="flex items-center justify-center py-12">
          <i class="pi pi-spin pi-spinner text-3xl text-primary"></i>
        </div>

        <Message v-else-if="errorSchool && !paymentPro.length" severity="error" data-testid="payment-providers-error">
          <div class="flex items-center justify-between gap-3 flex-wrap">
            <span>We couldn't load your payment providers. Check your connection and try again.</span>
            <Button label="Try again" size="small" text icon="pi pi-refresh" data-testid="btn-retry-payment-providers" @click="settle(refetchSchool())" />
          </div>
        </Message>

        <div v-else-if="!paymentPro.length" class="text-center py-12 text-gray-500" data-testid="payment-providers-empty">
          <i class="pi pi-credit-card text-4xl mb-3 block"></i>
          <p class="text-sm">No payment providers yet. Students and parents can only pay online once you add one.</p>
          <Button label="Add your first provider" size="small" text class="mt-2" data-testid="btn-add-first-provider" @click="toggleForm()" />
        </div>

        <div v-else class="space-y-3">
          <div v-for="provider in paymentPro" :key="provider.id ?? provider.name ?? ''"
            class="rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
            <div class="flex items-center justify-between gap-3 p-4">
              <div class="flex items-center gap-4 min-w-0">
                <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/10 text-primary shrink-0">
                  <i class="pi pi-wallet text-lg"></i>
                </div>
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-gray-900">{{ providerLabel(provider.name) }}</p>
                  <p class="text-xs text-gray-500">
                    {{ providerDescription(provider.name) }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-3 shrink-0">
                <Tag v-if="providerIsInTestMode(provider)" value="Test mode" severity="warn" data-testid="paystack-test-mode-chip" />
                <span class="text-xs text-gray-500">{{ provider.is_active ? 'On' : 'Off' }}</span>
                <InputSwitch
                  :key="`${provider.id}-${switchVersion}`"
                  :modelValue="!!provider?.is_active"
                  :disabled="togglingId === provider.id"
                  :aria-label="`${provider.is_active ? 'Switch off' : 'Switch on'} ${providerLabel(provider.name)}`"
                  @update:modelValue="(val: boolean) => requestToggle(provider, val)"
                  :data-testid="`toggle-provider-${provider.name}`"
                />
                <button
                  v-if="!isCode(provider?.name, PaymentProviderType.Interswitch)"
                  v-tooltip.top="'Change details'"
                  type="button"
                  :aria-label="`Change ${providerLabel(provider.name)} details`"
                  class="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors"
                  :data-testid="`btn-edit-provider-${provider.name}`"
                  @click="editProvider(provider)">
                  <i class="pi pi-pencil text-sm"></i>
                </button>
              </div>
            </div>
            <GatewaySettlementAccountsPanel
              :provider-id="provider.id ?? null"
              :provider-name="provider.name ?? undefined"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Provider setup guide -->
    <Dialog v-model:visible="showGuide" modal :header="activeGuide?.title ?? 'Setup guide'"
      :style="{ width: '34rem', maxWidth: '95vw' }" data-testid="payment-setup-guide-dialog">
      <div v-if="activeGuide" class="space-y-4 text-sm text-gray-700">
        <p>{{ activeGuide.intro }}</p>
        <ol class="space-y-3 list-decimal list-outside pl-5">
          <li v-for="(step, index) in activeGuide.steps" :key="index">
            <span class="font-semibold text-gray-900">{{ step.title }}</span>
            <p class="mt-0.5 text-gray-600">{{ step.detail }}</p>
          </li>
        </ol>
        <div class="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p class="text-xs font-medium text-gray-600 mb-1">Webhook URL to register</p>
          <div class="flex items-center gap-2">
            <code class="text-xs break-all text-gray-800" data-testid="payment-setup-guide-webhook">{{ guideWebhookUrl }}</code>
            <button type="button" class="shrink-0 p-1 rounded hover:bg-gray-200 text-gray-500"
              v-tooltip.top="'Copy webhook URL'" aria-label="Copy webhook URL" data-testid="btn-copy-webhook-url" @click="copyWebhookUrl()">
              <i class="pi pi-copy text-xs"></i>
            </button>
          </div>
          <p class="mt-1 text-xs text-gray-500">{{ activeGuide.webhookNote }}</p>
        </div>
        <a :href="activeGuide.dashboardUrl" target="_blank" rel="noopener noreferrer"
          class="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
          {{ activeGuide.dashboardLabel }}
          <i class="pi pi-external-link text-[11px]"></i>
        </a>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { PaymentProviderType } from '@/types/composition-functions';
import { usePaymentMutations } from '@/composables/mutations/payment/usePaymentMutations';
import { usePaymentProviderStatus } from '@/composables/queries/usePaymentProviderStatus';
import { useGatewaySettlementAccountList } from '@/composables/billing/useGatewaySettlementAccounts';
import {
  fieldPlaceholder,
  providerDescription,
  providerLabel,
  providerSetupRow,
  providerSetupRows,
  providerToggleWarning,
  usePaymentProviderForm,
  type SavedProvider,
} from '@/composables/billing/paymentProviderSetup';
import { isCode } from '@/utils/billingLabels';
import { isPaystackTestKey } from '@/utils/paymentKeys';
import { settle } from '@/utils/settle';
import { can, AppPermissions } from '@/utils/permissions';
import { useUserStore } from '@/store/userStore';
import { setupGuideFor, webhookUrlFor, type PaymentSetupGuide } from './paymentSetupGuides';
import GatewaySettlementAccountsPanel from './GatewaySettlementAccountsPanel.vue';

const toast = useToast();
const confirm = useConfirm();
const userStore = useUserStore();
const { setPaymentProvider, togglePaymentProvider } = usePaymentMutations();
const { paymentProviders, loadingSchool, refetchSchool, errorSchool } = useSchoolQuery();
const { providerStatuses, loadingProviderStatus, refetchProviderStatus } = usePaymentProviderStatus();

const paymentPro = computed<SavedProvider[]>(
  () => ((paymentProviders.value || []) as Array<SavedProvider | null>).filter((provider): provider is SavedProvider => !!provider),
);

const {
  name: formName,
  editing: isEditing,
  values: formValues,
  fields: formFields,
  options: providerOptions,
  allProvidersAdded,
  selectProvider,
  reset: resetForm,
  startEdit,
  isSaved,
  fieldError,
  nameError,
  submit,
} = usePaymentProviderForm(providerStatuses, paymentPro);

const showForm = ref(false);
const isSubmitting = ref(false);

// Interswitch is set up through PAY Items, so its status comes from them
// (the same cached list the PAY Items panel shows).
const canViewPayItems = computed(() =>
  can(userStore.currentUser, AppPermissions.Billing.Actions.ManageGatewaySettlementAccounts, AppPermissions.Billing.Resource)
  || can(userStore.currentUser, AppPermissions.Billing.Actions.ViewGatewaySettlementAccounts, AppPermissions.Billing.Resource),
);
const interswitchProviderId = computed(() => {
  if (!canViewPayItems.value) return null;
  return paymentPro.value.find((provider) => isCode(provider.name, PaymentProviderType.Interswitch))?.id ?? null;
});
const { accounts: interswitchPayItems, loaded: payItemsLoaded } = useGatewaySettlementAccountList(interswitchProviderId);
const interswitchPayItemCount = computed<number | null | undefined>(() => {
  if (!canViewPayItems.value) return undefined;
  if (!interswitchProviderId.value || !payItemsLoaded.value) return null;
  return interswitchPayItems.value.filter((item) => item.is_active).length;
});

const setupRows = computed(() => providerSetupRows(paymentPro.value, providerStatuses.value, interswitchPayItemCount.value));

// Provider setup guide (issue #240): a school-facing "how to configure this
// provider" panel, opened from the setup-status list or the form.
const showGuide = ref(false);
const activeGuide = ref<PaymentSetupGuide | null>(null);
const guideWebhookUrl = computed(() => (activeGuide.value ? webhookUrlFor(activeGuide.value) : ''));

function openSetupGuide(name: string) {
  const guide = setupGuideFor(name);
  if (!guide) return;
  activeGuide.value = guide;
  showGuide.value = true;
}

async function copyWebhookUrl() {
  try {
    await navigator.clipboard?.writeText(guideWebhookUrl.value);
    toast.add({ severity: 'success', summary: 'Webhook URL copied', detail: 'Paste it into your provider\'s dashboard.', life: 3000 });
  } catch {
    // Clipboard may be unavailable (insecure context); the URL is still visible to copy manually.
  }
}

// ── Switching providers on and off ──

const togglingId = ref<string | null>(null);
// Remounts the switches when a change is cancelled or refused, so a switch
// never stays flipped while the provider did not change.
const switchVersion = ref(0);
const activeCount = computed(() => paymentPro.value.filter((provider) => provider.is_active).length);

async function runToggle(provider: SavedProvider, turnOn: boolean) {
  if (!provider.id) return;
  togglingId.value = provider.id;
  try {
    // The mutation returns id + is_active, so the cached row (and this list)
    // update in place — no full reload of the school.
    await togglePaymentProvider(provider.id, turnOn, provider.name);
  } catch {
    switchVersion.value += 1; // toast shown by the composable
  } finally {
    togglingId.value = null;
  }
}

function requestToggle(provider: SavedProvider, turnOn: boolean) {
  if (!provider.id || togglingId.value) {
    switchVersion.value += 1;
    return;
  }
  const row = setupRows.value.find((candidate) => candidate.id === provider.id)
    ?? providerSetupRow(provider, undefined, interswitchPayItemCount.value);
  const warning = providerToggleWarning(row, turnOn, activeCount.value);
  if (!warning) {
    void runToggle(provider, turnOn);
    return;
  }
  confirm.require({
    header: warning.header,
    message: warning.message,
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: warning.acceptLabel,
    rejectProps: { label: 'Cancel', severity: 'secondary', outlined: true },
    acceptClass: turnOn ? undefined : 'p-button-danger',
    accept: () => { void runToggle(provider, turnOn); },
    reject: () => { switchVersion.value += 1; },
    onHide: () => { switchVersion.value += 1; },
  });
}

// ── Add / edit ──

function toggleForm() {
  resetForm();
  showForm.value = !showForm.value;
}

function editProvider(provider: SavedProvider) {
  startEdit(provider);
  showForm.value = true;
}

async function submitProvider() {
  isSubmitting.value = true;
  try {
    const saved = await submit((input) => setPaymentProvider(input));
    if (!saved) return;
    showForm.value = false;
    resetForm();
    // A newly added provider is not in the cached school list yet.
    await Promise.allSettled([refetchSchool(), refetchProviderStatus()]);
  } finally {
    isSubmitting.value = false;
  }
}

// Paystack issues pk_test_/sk_test_ keys for its test environment — surface
// that so a school can run a full end-to-end test before going live (#229).
const paystackFormInTestMode = computed(
  () =>
    isCode(formName.value, PaymentProviderType.Paystack)
    && (isPaystackTestKey(formValues.publicKey) || isPaystackTestKey(formValues.secretKey)),
);

function providerIsInTestMode(provider: SavedProvider): boolean {
  const config = provider.config as { publicKey?: string | null } | null | undefined;
  return isCode(provider.name, PaymentProviderType.Paystack) && isPaystackTestKey(config?.publicKey ?? null);
}
</script>
